import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { LoadingScreen } from '../../../components/loading-screen';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardPageProps } from '../../../lib/dashboard-page';

const PAGE = 'features:getting_ready.pages.getting_ready_page' satisfies I18nPrefix;

/** How often to recheck whether the site is ready. */
const POLL_INTERVAL_MS = 2500;
/** Give up and show an error after this long. */
const MAX_WAIT_MS = 90_000;

type GettingReadyPageProps = {
  firstName?: string;
};

export function GettingReadyPage({
  handlers,
  pageProps = {},
}: DashboardPageProps<GettingReadyPageProps>) {
  const { envHandler, navigationHandler } = handlers;
  const { firstName } = pageProps;
  const [timedOut, setTimedOut] = useState<boolean>(false);

  useEffect(() => {
    let stopped = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const stop = () => {
      stopped = true;
      if (intervalId !== undefined) clearInterval(intervalId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };

    const check = async () => {
      const status = await envHandler.reload();
      if (stopped || !status.isReady) return;

      // Redirect once, so stop the interval and timeout first.
      stop();

      if (status.hasPlatform || status.isPartner) {
        // The dashboard root picks the right landing page for the team.
        navigationHandler.navigate('/', { excludeSiteId: true, excludeSiteEnv: true });
      } else {
        navigationHandler.navigate('/integrations/platform');
      }
    };

    // Check straight away so a ready site doesn't wait a full interval.
    void check();
    intervalId = setInterval(check, POLL_INTERVAL_MS);
    timeoutId = setTimeout(() => {
      stop();
      setTimedOut(true);
    }, MAX_WAIT_MS);

    return stop;
  }, [envHandler, navigationHandler]);

  if (timedOut) {
    return <GettingReadyTimeout />;
  }

  return <LoadingScreen message={t(`${PAGE}.message`, { firstName })} />;
}

/** Shown when the site isn't ready within {@link MAX_WAIT_MS}. */
export function GettingReadyTimeout() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-spark-surface-page">
      <div className="m-auto block max-w-md p-6 text-center">
        <p className="font-bold text-spark-text-primary sm:text-xl">
          {t(`${PAGE}.timeout_heading`)}
        </p>
        <p className="mt-2 text-sm text-spark-text-secondary">{t(`${PAGE}.timeout_message`)}</p>
        <button
          type="button"
          className="mt-6 inline-flex items-center justify-center rounded-md border border-spark-border-subtle bg-indigo-600 px-4 py-2 font-medium text-sm text-white shadow-xs hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-spark-accent-hover focus:ring-offset-2"
          onClick={() => window.location.reload()}
        >
          {t(`${PAGE}.timeout_retry`)}
        </button>
        <p className="mt-4 text-sm">
          <a
            href="https://docs.sparklayer.io/support"
            className="font-medium text-spark-accent hover:text-spark-accent-hover"
          >
            {t(`${PAGE}.timeout_support`)}
          </a>
        </p>
      </div>
    </div>
  );
}
